import PageComponent from "../../components/page/PageComponent.vue";
import SectionSheet from "../../components/section_sheet/SectionSheet.vue";
import PageControl from "../../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "IndividualBankAccountReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: '.sheet',
      required: false
    },
    bank_account_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        account_information: false,
        verification_results: false,
        transaction_details: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some(option => option);
    },
    safe_bank_account_data() {
      return this.bank_account_data || {};
    },
    
    // Account Verification Result helpers
    account_verification_result() {
      return this.safe_bank_account_data.AccountVerificationResult || {};
    },
    
    result_file() {
      return this.account_verification_result.ResultFile || {};
    },
    
    subscriber_input_details() {
      return this.account_verification_result.SubscriberInputDetails || {};
    },
    
    // Basic Account Information
    account_holder_name() {
      const initials = this.result_file.INITIALS || '';
      const surname = this.result_file.SURNAME || '';
      return `${initials} ${surname}`.trim() || 'N/A';
    },
    
    id_number() {
      return this.result_file.IDNUMBER || 'N/A';
    },
    
    account_number() {
      return this.result_file.ACCOUNTNUMBER || 'N/A';
    },
    
    bank_name() {
      return this.subscriber_input_details.BankName || 'N/A';
    },
    
    branch_number() {
      return this.result_file.BRANCHNUMBER || 'N/A';
    },
    
    account_type() {
      return this.subscriber_input_details.AccountType || 'N/A';
    },
    
    // Transaction Details
    xds_ref_no() {
      return this.subscriber_input_details.XDsRefNo || 'N/A';
    },
    
    enquiry_status() {
      return this.subscriber_input_details.EnquiryStatus || 'N/A';
    },
    
    enquiry_type() {
      return this.subscriber_input_details.EnquiryType || 'N/A';
    },
    
    subscriber_name() {
      return this.subscriber_input_details.SubscriberName || 'N/A';
    },
    
    ref_number() {
      return this.subscriber_input_details.refnumber || 'N/A';
    },
    
    enquiry_date() {
      return this.subscriber_input_details.EnquiryDate || new Date().toISOString();
    },
    
    formatted_enquiry_date() {
      return this.formatDate(this.enquiry_date);
    },
    
    // Verification Results
    account_found() {
      return this.result_file.ACCOUNTFOUND || 'N/A';
    },
    
    id_number_match() {
      return this.result_file.IDNUMBERMATCH || 'N/A';
    },
    
    initials_match() {
      return this.result_file.INITIALSMATCH || 'N/A';
    },
    
    surname_match() {
      return this.result_file.SURNAMEMATCH || 'N/A';
    },
    
    account_open() {
      return this.result_file['ACCOUNT-OPEN'] || 'N/A';
    },
    
    account_open_three_months() {
      return this.result_file.ACCOUNTOPENFORATLEASTTHREEMONTHS || 'N/A';
    },
    
    account_accepts_debits() {
      return this.result_file.ACCOUNTACCEPTSDEBITS || 'N/A';
    },
    
    account_accepts_credits() {
      return this.result_file.ACCOUNTACCEPTSCREDITS || 'N/A';
    },
    
    // Additional Account Details
    account_type_return() {
      return this.result_file.ACCOUNTTYPERETURN || 'N/A';
    },
    
    account_dormant() {
      return this.result_file.ACCOUNTDORMANT || 'Not Available';
    },
    
    tax_reference_match() {
      return this.result_file.TAXREFERENCEMATCH || 'Not Available';
    },
    
    email_match() {
      return this.result_file.EMAILMATCH || 'Not Available';
    },
    
    phone_match() {
      return this.result_file.PHONEMATCH || 'Not Available';
    },
    
    // Contact Information
    email_address() {
      return this.result_file.EMAILADDRESS || null;
    },
    
    contact_number() {
      return this.result_file.CONTACTNUMBER || null;
    },
    
    // Report metadata
    report_reference() {
      return this.xds_ref_no;
    },
    
    // Badge classes and icons
    verification_status_badge_class() {
      return this.enquiry_status === 'Verified' ? 'status-good' : 'status-bad';
    },
    
    verification_status_icon() {
      return this.enquiry_status === 'Verified' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    
    modal_instance() {
      var modal = new window.bootstrap.Modal(document.getElementById(`export-modal-${this.report_reference}`))
      return modal;
    }
  },
  
  methods: {
    close_modal() {
      this.modal_instance.hide();
    },
    
    // Utility methods
    formatDate(date) {
      return format_date(date);
    },
    
    get_verification_badge_class(result) {
      return result === 'Yes' ? 'bg-success' : 'bg-danger';
    },
    
    get_verification_icon(result) {
      return result === 'Yes' ? 'fas fa-check' : 'fas fa-times';
    },
    
    // Export functionality
    show_export_modal() {
      this.export_options.account_information = true;
      this.export_options.verification_results = true;
      this.export_options.transaction_details = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },
    
    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      }
      
      if (!this.libraries_loaded.sheetjs) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      }
    },
    
    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    async export_data() {
      await this.load_export_libraries();
      
      if (this.export_format === 'csv') {
        await this.export_csv();
      } else if (this.export_format === 'xlsx') {
        await this.export_excel();
      } else if (this.export_format === 'json') {
        await this.export_json();
      }
      
      // Close modal
      this.close_modal();
    },
    
    // Prepare account information data
    prepare_account_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Account Holder": this.account_holder_name,
          "ID Number": this.id_number,
          "Account Number": this.account_number,
          "Bank": this.bank_name,
          "Branch Code": this.branch_number,
          "Account Type": this.account_type,
          "Verification Status": this.enquiry_status,
          "Enquiry Type": this.enquiry_type,
          "Subscriber": this.subscriber_name,
          "Internal Reference": this.ref_number,
          "Generated Date": this.formatted_enquiry_date
        }
      ];
    },
    
    // Prepare verification results data
    prepare_verification_results_data() {
      return [
        {
          "Verification Check": "Account Found",
          "Description": "Verify if account exists in banking system",
          "Result": this.account_found,
          "Status": this.account_found === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "ID Number Match",
          "Description": "Verify if ID number matches account holder",
          "Result": this.id_number_match,
          "Status": this.id_number_match === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Initials Match",
          "Description": "Verify if initials match account holder",
          "Result": this.initials_match,
          "Status": this.initials_match === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Surname Match",
          "Description": "Verify if surname matches account holder",
          "Result": this.surname_match,
          "Status": this.surname_match === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Account Open",
          "Description": "Verify if account is currently active",
          "Result": this.account_open,
          "Status": this.account_open === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Account Age (3+ Months)",
          "Description": "Verify if account has been open for at least 3 months",
          "Result": this.account_open_three_months,
          "Status": this.account_open_three_months === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Accepts Debits",
          "Description": "Verify if account can receive debit transactions",
          "Result": this.account_accepts_debits,
          "Status": this.account_accepts_debits === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Accepts Credits",
          "Description": "Verify if account can receive credit transactions",
          "Result": this.account_accepts_credits,
          "Status": this.account_accepts_credits === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Account Type Return",
          "Description": "Indicates if account type information was returned",
          "Result": this.account_type_return,
          "Status": this.account_type_return === 'Yes' ? 'Pass' : 'Fail'
        },
        {
          "Verification Check": "Account Dormant",
          "Description": "Status of account dormancy",
          "Result": this.account_dormant,
          "Status": this.account_dormant === 'Not Available' ? 'N/A' : 'Available'
        },
        {
          "Verification Check": "Tax Reference Match",
          "Description": "Tax reference number verification status",
          "Result": this.tax_reference_match,
          "Status": this.tax_reference_match === 'Not Available' ? 'N/A' : 'Available'
        },
        {
          "Verification Check": "Email Match",
          "Description": "Email address verification status",
          "Result": this.email_match,
          "Status": this.email_match === 'Not Available' ? 'N/A' : 'Available'
        },
        {
          "Verification Check": "Phone Match",
          "Description": "Phone number verification status",
          "Result": this.phone_match,
          "Status": this.phone_match === 'Not Available' ? 'N/A' : 'Available'
        }
      ];
    },
    
    // Prepare transaction details data
    prepare_transaction_details_data() {
      return [
        {
          "Transaction Reference": this.xds_ref_no,
          "Enquiry Date": this.formatted_enquiry_date,
          "Enquiry Type": this.enquiry_type,
          "Enquiry Status": this.enquiry_status,
          "Subscriber Name": this.subscriber_name,
          "Subscriber Username": this.subscriber_input_details.SubscriberUserName || 'N/A',
          "Internal Reference": this.ref_number,
          "External Reference": this.subscriber_input_details.ExternalRef || 'N/A',
          "Enquiry Input": this.subscriber_input_details.EnquiryInput || 'N/A',
          "Account Holder Requested": this.account_holder_name,
          "ID Number Requested": this.id_number,
          "Account Number Requested": this.account_number,
          "Bank Requested": this.bank_name,
          "Branch Code Requested": this.branch_number,
          "Account Type Requested": this.account_type,
          "Email Address Requested": this.subscriber_input_details.EmailAddress || 'N/A',
          "Contact Number Requested": this.subscriber_input_details.ContactNumber || 'N/A',
          "Requester First Name": this.subscriber_input_details.RequesterFirstName || 'N/A',
          "Requester Surname": this.subscriber_input_details.RequesterSurName || 'N/A'
        }
      ];
    },
    
    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          xds_reference: this.xds_ref_no,
          generated_date: this.formatted_enquiry_date,
          export_date: new Date().toISOString(),
          report_type: 'Account Verification Report'
        },
        raw_account_verification_data: this.safe_bank_account_data
      };
    },
    
    // Validation method to test export data completeness
    validate_export_data() {
      const validation_report = {
        account_information: {
          available: !!this.account_holder_name && !!this.id_number && !!this.account_number,
          data_count: this.prepare_account_information_data().length
        },
        verification_results: {
          available: !!this.account_found,
          data_count: this.prepare_verification_results_data().length
        },
        transaction_details: {
          available: !!this.xds_ref_no && !!this.enquiry_status,
          data_count: this.prepare_transaction_details_data().length
        }
      };
      
      return validation_report;
    },
    
    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split('T')[0];
      
      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: 'account_information',
          label: 'Account_Information',
          prepare: this.prepare_account_information_data
        },
        {
          key: 'verification_results',
          label: 'Verification_Results',
          prepare: this.prepare_verification_results_data
        },
        {
          key: 'transaction_details',
          label: 'Transaction_Details',
          prepare: this.prepare_transaction_details_data
        }
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `Account_Verification_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, 'text/csv');
          }
        }
      }
    },
    
    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split('T')[0];
      const workbook = XLSX.utils.book_new();
      
      if (this.export_options.account_information) {
        const account_data = this.prepare_account_information_data();
        const worksheet = XLSX.utils.json_to_sheet(account_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Account Information');
      }
      
      if (this.export_options.verification_results) {
        const verification_data = this.prepare_verification_results_data();
        const worksheet = XLSX.utils.json_to_sheet(verification_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Verification Results');
      }
      
      if (this.export_options.transaction_details) {
        const transaction_data = this.prepare_transaction_details_data();
        const worksheet = XLSX.utils.json_to_sheet(transaction_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transaction Details');
      }
      
      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            'JSON Data': JSON.stringify(json_data, null, 2)
          }
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Raw JSON Data');
      }
      
      const filename = `Account_Verification_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
    
    async export_json() {
      const current_date = new Date().toISOString().split('T')[0];
      const export_data = {};
      
      if (this.export_options.account_information) {
        export_data.account_information = this.prepare_account_information_data()[0];
      }
      
      if (this.export_options.verification_results) {
        export_data.verification_results = this.prepare_verification_results_data();
      }
      
      if (this.export_options.transaction_details) {
        export_data.transaction_details = this.prepare_transaction_details_data()[0];
      }
      
      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }
      
      const json = JSON.stringify(export_data, null, 2);
      const filename = `Account_Verification_Report_${current_date}.json`;
      this.download_file(json, filename, 'application/json');
    },
    
    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
